import React from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { MapPin, Sparkles, ArrowRight } from 'lucide-react';
import { useRest, RCard, RStat, RHead, RestLoading, RestPageBrief, SourceBadge, money, money2, RestReconnect } from './restui';
import { ToastMark, DoorDashMark, UberEatsMark, GrubhubMark, SquareMark, CloverMark } from '../../components/BrandIcons';

/* Mission Control — the restaurant talks to you. $500/mo software feel, zero fake data. */

const SOURCE_MARKS = { toast: ToastMark, doordash: DoorDashMark, ubereats: UberEatsMark, grubhub: GrubhubMark, square: SquareMark, clover: CloverMark };
const WX = (c) => (c === 0 ? '☀️' : c <= 2 ? '🌤️' : c === 3 ? '☁️' : c <= 48 ? '🌫️' : c <= 67 ? '🌧️' : c <= 77 ? '🌨️' : c <= 82 ? '🌦️' : '⛈️');

function greeting(isEs, name) {
  const h = new Date().getHours();
  const g = h < 12 ? (isEs ? 'Buenos días' : 'Good morning') : h < 18 ? (isEs ? 'Buenas tardes' : 'Good afternoon') : (isEs ? 'Buenas noches' : 'Good evening');
  return `${g}, ${name}`;
}

export function Header({ isEs, me, d }) {
  const full = me.user?.greeting || (me.user?.name || me.user?.email || '').split(' ')[0];
  const [name, byline] = full.split(/ by (.+)/);
  const w = d?.weather;
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-7">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-bold">
          {new Date().toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <h1 className="mt-1 text-[28px] lg:text-[32px] font-semibold tracking-tight text-gray-900" data-testid="rest-greeting">{greeting(isEs, name)}</h1>
        {byline && (
          <p className="mt-0.5 text-[13px] font-semibold text-[var(--rest-accent)] tracking-wide" data-testid="rest-greeting-byline">by {byline}</p>
        )}
        {me.user?.bio ? (
          <p className="mt-1 text-[12.5px] text-gray-500 italic max-w-none pr-2" data-testid="rest-host-bio">{me.user.bio[isEs ? 'es' : 'en']}</p>
        ) : (
          <p className="mt-1 text-[13.5px] text-gray-500">{isEs ? 'Tu restaurante, en tiempo real. Esto es lo que importa hoy.' : 'Your restaurant, live. Here is what matters today.'}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {d?.city && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12px] text-gray-600" data-testid="rest-location-pill">
            <MapPin className="w-3.5 h-3.5 text-[var(--rest-accent)]" /> {d.city}
          </span>
        )}
        {w && typeof w.temp === 'number' && (
          <span className="inline-flex flex-nowrap items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12px] text-gray-600 whitespace-nowrap" data-testid="rest-weather-pill">
            {WX(w.code)} <b className="text-gray-900 whitespace-nowrap">{Math.round(w.temp * 9 / 5 + 32)}°F / {Math.round(w.temp)}°C</b>
            {w.days?.[1] && (
              <span className="inline-flex flex-nowrap items-center gap-1 text-gray-400 border-l border-gray-200 pl-2 whitespace-nowrap" data-testid="rest-weather-tomorrow">
                {isEs ? 'mañana' : 'tmrw'} {WX(w.days[1].code)}
                <b className="text-gray-700">{Math.round(w.days[1].min * 9 / 5 + 32)}–{Math.round(w.days[1].max * 9 / 5 + 32)}°F</b>
                <span>{' · '}{Math.round(w.days[1].min)}–{Math.round(w.days[1].max)}°C</span>
              </span>
            )}
          </span>
        )}
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold ${d?.health === 'healthy' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="rest-health-badge" style={{ display: d?.health ? undefined : 'none' }}>
          <span className={`w-2 h-2 rounded-full ${d?.health === 'healthy' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
          {d?.health === 'healthy' ? (isEs ? 'Restaurante saludable' : 'Restaurant healthy') : (isEs ? 'Requiere atención' : 'Needs attention')}
        </span>
      </div>
    </div>
  );
}

export function ConnectTile({ icon: Icon, label, isEs, testid }) {
  return (
    <Link to="/restaurant/connections" data-testid={testid}
      className="group flex items-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white/60 px-4 py-3.5 hover:border-[var(--rest-accent)] transition-colors">
      <span className="w-9 h-9 rounded-xl bg-gray-100 group-hover:bg-[var(--rest-accent-soft)] flex items-center justify-center transition-colors">
        <Icon className="w-4 h-4 text-gray-400 group-hover:text-[var(--rest-accent)] transition-colors" />
      </span>
      <span className="flex-1">
        <span className="block text-[13px] font-semibold text-gray-700">{label}</span>
        <span className="block text-[11px] text-gray-400">{isEs ? 'No conectado' : 'Not connected'}</span>
      </span>
      <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-gray-400 group-hover:text-[var(--rest-accent)] transition-colors">
        {isEs ? 'Conectar' : 'Connect'} <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}

export function DailyIntel({ isEs }) {
  const { data: d } = useRest('/daily');
  if (!d || (!d.actions?.length && !d.calendar?.length)) return null;
  const dot = { hot: 'bg-red-500', warm: 'bg-amber-500', info: 'bg-sky-500' };
  const srcLabel = { live_weather: isEs ? 'Clima en vivo' : 'Live weather', playbook: 'Playbook', manual_imports: isEs ? 'Tus datos' : 'Your data' };
  return (
    <div className="mt-5 grid grid-cols-1 lg:grid-cols-5 gap-4">
      <RCard className="p-6 lg:col-span-3" testid="rest-daily-actions">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-4">{isEs ? 'Acciones de hoy' : "Today's Actions"}</p>
        <div className="space-y-3.5">
          {d.actions.map((a) => (
            <div key={a.id} className="flex gap-3">
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot[a.urgency] || 'bg-gray-300'}`} />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-gray-900">{isEs ? a.title_es : a.title_en}</p>
                <p className="text-[12.5px] text-gray-500 leading-relaxed">{isEs ? a.body_es : a.body_en}</p>
                <div className="mt-1 flex items-center gap-3 flex-wrap">
                  <p className="text-[10px] uppercase tracking-wider text-gray-300 font-bold">{srcLabel[a.source] || a.source}</p>
                  {a.action && (a.action.to ? (
                    <Link to={a.action.to} data-testid={`rest-action-cta-${a.id}`}
                      className="text-[11.5px] font-bold text-[var(--rest-accent)] hover:underline">
                      {isEs ? a.action.label_es : a.action.label_en} →
                    </Link>
                  ) : (
                    <a href={a.action.href} target="_blank" rel="noopener noreferrer" data-testid={`rest-action-cta-${a.id}`}
                      className="text-[11.5px] font-bold text-[#1DA851] hover:underline">
                      💬 {isEs ? a.action.label_es : a.action.label_en} →
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </RCard>
      <RCard className="p-6 lg:col-span-2" testid="rest-sales-calendar">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-4">{isEs ? 'Calendario de ventas' : 'Sales Calendar'}</p>
        <div className="space-y-3">
          {d.calendar.map((c) => (
            <div key={c.date} className={`flex items-start gap-3 rounded-xl border p-3 ${c.in_days <= 14 ? 'border-[var(--rest-accent)] bg-[var(--rest-accent-soft)]' : 'border-gray-100 bg-white'}`}>
              <span className="w-11 shrink-0 text-center rounded-lg border border-gray-200 bg-white py-1">
                <span className="block text-[9px] uppercase text-gray-400 font-bold">{new Date(`${c.date}T12:00:00`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { month: 'short' })}</span>
                <span className="block text-[15px] font-bold text-gray-900 leading-tight">{c.date.slice(8, 10)}</span>
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="text-[13px] text-gray-800 font-semibold truncate">{c.name}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${c.in_days <= 14 ? 'bg-[var(--rest-accent)] text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {c.in_days === 0 ? (isEs ? '¡HOY!' : 'TODAY!') : (isEs ? `${c.in_days} días` : `${c.in_days} days`)}
                  </span>
                </span>
                {(isEs ? c.tip_es : c.tip_en) && <span className="block mt-0.5 text-[11.5px] text-gray-500 leading-snug">💡 {isEs ? c.tip_es : c.tip_en}</span>}
                {c.in_days <= 30 && (
                  <a href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? `Quiero preparar una campaña para ${c.name}` : `I want to prep a campaign for ${c.name}`)}`}
                    target="_blank" rel="noopener noreferrer" data-testid={`rest-calendar-cta-${c.date}`}
                    className="mt-1 inline-block text-[11.5px] font-bold text-[#1DA851] hover:underline">
                    💬 {isEs ? 'Prepararlo con mi AI Team →' : 'Prep it with my AI Team →'}
                  </a>
                )}
              </span>
            </div>
          ))}
        </div>
      </RCard>
    </div>
  );
}

export function MonthlyChart({ monthly, isEs, kitxensSince }) {
  if (!monthly?.length) return null;
  const mobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const rows = mobile ? monthly.slice(-6) : monthly;
  const max = Math.max(...rows.map((m) => m.total)) || 1;
  const label = (m) => `${new Date(`${m}-15`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { month: 'short' })} ’${m.slice(2, 4)}`;
  const active = (m) => !kitxensSince || m >= kitxensSince;
  const firstActive = kitxensSince ? rows.find((m) => active(m.month))?.month : null;
  return (
    <RCard className="p-4 sm:p-6 mt-5" testid="rest-monthly-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">
          {isEs ? 'Histórico mes a mes' : 'Month-by-month history'}
          {mobile && <span className="ml-1.5 normal-case tracking-normal text-gray-300 font-semibold">· {isEs ? 'últimos 6' : 'last 6'}</span>}
        </p>
        <SourceBadge source="manual_imports" isEs={isEs} />
      </div>
      <div className="flex items-end gap-2 sm:gap-4">
        {rows.map((m, i) => (
          <div key={m.month}
            className={`flex-1 flex flex-col items-center gap-1.5 sm:gap-2 min-w-0 ${m.month === firstActive && i > 0 ? 'border-l-2 border-dashed border-[var(--rest-accent)]/60 pl-1.5 sm:pl-2.5' : ''}`}
            title={`${m.month}: ${money2(m.total)} · ${m.orders} orders${kitxensSince && m.month === firstActive ? (isEs ? ' · Kitxens activado' : ' · Kitxens activated') : ''}`}>
            <span className="text-[10px] sm:text-[12px] font-semibold tabular-nums text-gray-900">{money(m.total)}</span>
            <div className={`w-full max-w-[72px] rounded-t-lg transition-opacity kx-grow-y ${active(m.month) ? 'bg-[var(--rest-accent)] opacity-85 hover:opacity-100' : 'bg-gray-300 opacity-70 hover:opacity-90'}`}
              style={{ height: `${Math.max(10, Math.round(120 * m.total / max))}px`, animationDelay: `${i * 55}ms` }} />
            <span className="text-[10px] sm:text-[11px] text-gray-400 capitalize truncate max-w-full">{label(m.month)}</span>
            <span className="hidden sm:block text-[10px] text-gray-400 tabular-nums">{m.orders.toLocaleString()} {isEs ? 'órd.' : 'ord.'}</span>
          </div>
        ))}
      </div>
      {kitxensSince && (
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-semibold" data-testid="rest-kitxens-legend">
          <span className="inline-flex items-center gap-1.5 text-gray-400"><span className="w-2.5 h-2.5 rounded-sm bg-gray-300" /> {isEs ? 'Antes de Kitxens' : 'Before Kitxens'}</span>
          <span className="inline-flex items-center gap-1.5 text-[var(--rest-accent)]"><span className="w-2.5 h-2.5 rounded-sm bg-[var(--rest-accent)]" /> {isEs ? 'Con Kitxens AI' : 'With Kitxens AI'}</span>
          <span className="text-gray-400 font-medium">{isEs ? `Kitxens activado en ${label(kitxensSince)}` : `Kitxens activated in ${label(kitxensSince)}`}</span>
        </div>
      )}
    </RCard>
  );
}

export default function RestMissionControl() {
  const { isEs, me } = useOutletContext();
  const { data: d, err, loading } = useRest('/overview');
  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;

  if (!d.has_data) {
    return (
      <div data-testid="rest-mission-control">
        <RHead title="Mission Control" sub={isEs ? 'Tu operación de un vistazo.' : 'Your operation at a glance.'} />
        <RCard className="p-10 text-center" testid="rest-empty-state">
          <p className="text-[14px] text-gray-500 max-w-lg mx-auto">
            {isEs ? 'Aún no hay datos de ventas. Conecta un sistema o importa tu último reporte y este workspace cobrará vida.' : 'No sales data yet. Connect a system or import your latest report and this workspace will come alive.'}
          </p>
          <Link to="/restaurant/reports" data-testid="rest-goto-reports"
            className="mt-5 inline-block rounded-xl bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[14px] px-7 py-2.5 transition-opacity shadow-md">
            {isEs ? 'Conectar mi restaurante →' : 'Connect My Restaurant →'}
          </Link>
        </RCard>
      </div>
    );
  }

  const w = d.week; const p = d.prev_week;
  const delta = p.total > 0 ? (100 * (w.total - p.total) / p.total) : null;
  const totalMix = d.delivery_mix.reduce((a, x) => a + x.total, 0) || 1;
  const lastDayLabel = new Date(`${d.latest_day?.date}T12:00:00`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric' });

  return (
    <div data-testid="rest-mission-control">
      <RHead title="Mission Control" sub={isEs ? 'Tu operación de un vistazo — números, canales y tendencia.' : 'Your operation at a glance — numbers, channels and trend.'} />

      <RestPageBrief isEs={isEs} testid="rest-mc-brief"
        summary={isEs
          ? `Esta semana llevas ${money(w.total)} en ${w.orders.toLocaleString()} órdenes${delta != null ? ` (${delta >= 0 ? '+' : ''}${Math.round(delta)}% vs la semana anterior)` : ''}. El último día registrado (${lastDayLabel}) generó ${money(d.latest_day?.total)}.`
          : `This week you're at ${money(w.total)} across ${w.orders.toLocaleString()} orders${delta != null ? ` (${delta >= 0 ? '+' : ''}${Math.round(delta)}% vs previous week)` : ''}. The latest recorded day (${lastDayLabel}) generated ${money(d.latest_day?.total)}.`}
        alert={(d.alerts || []).find((a) => a.level === 'warn') ? (isEs ? (d.alerts.find((a) => a.level === 'warn').text_es) : (d.alerts.find((a) => a.level === 'warn').text_en)) : null}
        kpi={{ label: isEs ? 'Ventas · semana' : 'Sales · week', value: money(w.total),
          sub: delta != null ? `${delta >= 0 ? '▲' : '▼'} ${Math.abs(Math.round(delta))}% ${isEs ? 'vs anterior' : 'vs prev'}` : null, down: delta != null && delta < 0 }} />

      {(d.alerts || []).length > 0 && (
        <div className="mb-5 space-y-2" data-testid="rest-alerts">
          {d.alerts.map((a, i) => (
            <div key={i} className={`rounded-xl border px-4 py-2.5 text-[13px] ${a.level === 'warn' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-sky-300 bg-sky-50 text-sky-700'}`}>
              {a.level === 'warn' ? '⚠️' : 'ℹ️'} {isEs ? a.text_es : a.text_en}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RStat label={isEs ? `Ingresos · ${lastDayLabel} (último día)` : `Revenue · ${lastDayLabel} (latest day)`}
          value={money(d.latest_day?.total)} sub={`${d.latest_day?.orders || 0} ${isEs ? 'órdenes' : 'orders'}`} testid="rest-kpi-revenue-day" />
        <RStat label={isEs ? 'Ventas · semana' : 'Sales · week'}
          value={money(w.total)} delta={delta} sub={isEs ? 'vs semana anterior' : 'vs previous week'} testid="rest-kpi-sales" />
        <RStat label={isEs ? 'Órdenes · semana' : 'Orders · week'} value={w.orders.toLocaleString()}
          sub={`${money2(w.avg_ticket)} ${isEs ? 'ticket promedio' : 'avg ticket'}`} testid="rest-kpi-orders" />
        <RStat label={isEs ? 'Más vendido · 30 días' : 'Top seller · 30 days'}
          value={d.top_item ? d.top_item.name : '—'}
          sub={d.top_item ? `${money(d.top_item.total)} · ${d.top_item.orders} ${isEs ? 'órdenes' : 'orders'}` : (isEs ? 'sin datos de ítems' : 'no item data')} testid="rest-kpi-top-item" />
      </div>

      <MonthlyChart monthly={d.monthly} isEs={isEs} kitxensSince={d.kitxens_since} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RCard className="p-6" testid="rest-mix-card">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-4">{isEs ? 'Mix de canales' : 'Channel mix'}</p>
          {d.delivery_mix.map((c) => (
            <div key={c.key} className="mb-3">
              <div className="flex justify-between text-[12.5px] mb-1">
                <span className="capitalize text-gray-600">{c.key === 'pos' ? (isEs ? 'En local (POS)' : 'In-store (POS)') : c.key}</span>
                <span className="tabular-nums text-gray-900 font-semibold">{money(c.total)} · {Math.round(100 * c.total / totalMix)}%</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-[var(--rest-accent)]" style={{ width: `${Math.max(3, 100 * c.total / totalMix)}%` }} />
              </div>
            </div>
          ))}
          <SourceBadge source="manual_imports" isEs={isEs} className="mt-2" />
        </RCard>
        <RCard className="p-6" testid="rest-sources-card">
          <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-4">{isEs ? 'Por plataforma' : 'By platform'}</p>
          {d.by_source.map((s) => {
            const Mark = SOURCE_MARKS[s.key];
            return (
              <div key={s.key} className="flex items-center gap-2.5 py-2.5 border-b border-gray-100 last:border-0">
                {Mark ? <Mark className="w-5 h-5" /> : <span className="w-5 h-5 rounded bg-gray-200" />}
                <span className="capitalize text-[13px] text-gray-700 font-medium">{s.key}</span>
                <span className="ml-auto tabular-nums text-[13px] text-gray-900 font-semibold">{money(s.total)} <span className="text-gray-400 font-normal">· {s.orders} {isEs ? 'órdenes' : 'orders'}</span></span>
              </div>
            );
          })}
        </RCard>
      </div>

      <Link to="/restaurant/ai-executive" data-testid="rest-goto-brief"
        className="mt-5 group flex items-center gap-4 rounded-2xl bg-[#1A1A1A] px-6 py-5 hover:bg-[#242424] transition-colors">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--rest-accent)' }}>
          <Sparkles className="w-5 h-5 text-white" />
        </span>
        <span className="flex-1">
          <span className="block text-[14.5px] font-semibold text-white">AI Executive Brief</span>
          <span className="block text-[12px] text-white/60">{isEs ? 'Tu resumen ejecutivo: qué pasó, por qué, y qué hacer a continuación.' : 'Your executive summary: what happened, why, and what to do next.'}</span>
        </span>
        <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
      </Link>
    </div>
  );
}
